import { useEffect, useState } from 'react';
import type { JSX } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import classNames from 'classnames';
import { useToast } from '@admin-platform/ui/components';

import { Button } from '../../../components/Button/Button';
import Checkbox from '../../../components/Checkbox/Checkbox';
import { SingleSelect } from '../../../components/Inputs/SingleSelect';
import { Label } from '../../../components/Inputs/Label';
import { Skeleton } from '../../../components/Skeleton/Skeleton';

import styles from './Notifications.module.css';

import NOTIFICATION_SETTINGS from './queries/notificationSettings.query.graphql';
import type { NotificationSettingsQuery } from './queries/notificationSettings.query.graphql';
import UPDATE_DEFAULTS from './mutations/updateTenantNotificationDefaults.mutation.graphql';
import type {
  UpdateTenantNotificationDefaultsMutation,
  UpdateTenantNotificationDefaultsMutationVariables,
} from './mutations/updateTenantNotificationDefaults.mutation.graphql';

/** ISO weekday, Monday first — the same order the backend uses. */
export const WEEKDAYS = [
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
  { label: 'Sunday', value: 7 },
];

export const HOURS = Array.from({ length: 24 }, (_, hour) => ({
  label: `${String(hour).padStart(2, '0')}:00`,
  value: hour,
}));

/**
 * Org settings → Notifications.
 *
 * Two things live here: the workspace default schedule, and the delivery
 * summary. The summary is not decoration — "0 people reached this week" is how
 * a broken connection announces itself, and without it a fortnight of silence
 * looks like a team that stopped caring.
 */
export default function Notifications(): JSX.Element {
  const toast = useToast();
  const { data, loading, refetch } = useQuery<NotificationSettingsQuery>(NOTIFICATION_SETTINGS, {
    fetchPolicy: 'cache-and-network',
  });
  const [updateDefaults, { loading: saving }] = useMutation<
    UpdateTenantNotificationDefaultsMutation,
    UpdateTenantNotificationDefaultsMutationVariables
  >(UPDATE_DEFAULTS);

  const defaults = data?.tenantNotificationDefaults;
  const stats = data?.deliveryStats;

  const [day, setDay] = useState<number>(1);
  const [hour, setHour] = useState<number>(9);
  const [ccLeads, setCcLeads] = useState(true);
  const [announceLearnings, setAnnounceLearnings] = useState(false);

  useEffect(() => {
    if (!defaults) return;
    setDay(defaults.checkInDay);
    setHour(defaults.checkInHour);
    setCcLeads(defaults.ccTeamLeadsOnDigest);
    setAnnounceLearnings(defaults.announceHypothesisLearnings);
  }, [defaults]);

  if (loading && !data) {
    return <Skeleton className={styles.Page} />;
  }

  const handleSave = async () => {
    try {
      await updateDefaults({
        variables: {
          checkInDay: day,
          checkInHour: hour,
          ccTeamLeadsOnDigest: ccLeads,
          announceHypothesisLearnings: announceLearnings,
        },
      });
      toast('Notification defaults saved', 'success');
      await refetch();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not save the defaults.', 'error');
    }
  };

  return (
    <div className={styles.Page}>
      <div>
        <h1 className={styles.Title}>Notifications</h1>
        <p className={styles.Subtitle}>
          When the workspace is prompted, and how much of it is actually arriving. Anyone can choose
          their own day and time on their account page; this is what applies until they do.
        </p>
      </div>

      <div className={styles.Card}>
        <h2 className={styles.SectionTitle}>Check-in rhythm</h2>
        <div className={styles.Row}>
          <div className={styles.Field}>
            <Label htmlFor="notif-day">Day</Label>
            <SingleSelect
              id="notif-day"
              options={WEEKDAYS}
              value={day}
              onChange={(v) => setDay(Number(v))}
            />
          </div>
          <div className={styles.Field}>
            <Label htmlFor="notif-hour">Time</Label>
            <SingleSelect
              id="notif-hour"
              options={HOURS}
              value={hour}
              onChange={(v) => setHour(Number(v))}
            />
          </div>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
        <p className={styles.Hint}>
          Sent at that hour in each person&apos;s own timezone, not the workspace&apos;s. A single
          workspace hour is the middle of the night for anyone a few zones away, and a prompt at 3am
          reads as the product being broken.
        </p>

        <div className={styles.CheckRow}>
          <Checkbox
            id="notif-cc-leads"
            checked={ccLeads}
            onChange={(e) => setCcLeads(e.target.checked)}
          />
          <Label htmlFor="notif-cc-leads">Copy team leads on the weekly digest</Label>
        </div>

        <div className={styles.CheckRow}>
          <Checkbox
            id="notif-announce-learnings"
            checked={announceLearnings}
            onChange={(e) => setAnnounceLearnings(e.target.checked)}
          />
          <Label htmlFor="notif-announce-learnings">
            Post each concluded test&apos;s learning to the announcement channel
          </Label>
        </div>
        <p className={styles.Hint}>
          Off by default. When it is on, every hypothesis test that gets a verdict is posted with
          its outcome and learning, so what one team found out reaches everyone else.
        </p>
      </div>

      {stats && (
        <div className={styles.Card}>
          <h2 className={styles.SectionTitle}>Last seven days</h2>
          <div className={styles.Stats}>
            <span className={styles.Stat}>
              <span className={classNames(styles.StatValue, styles.Reached)}>
                {stats.peopleReached}
              </span>
              <span className={styles.StatLabel}>people reached</span>
            </span>
            <span className={styles.Stat}>
              <span className={styles.StatValue}>{stats.sent}</span>
              <span className={styles.StatLabel}>messages sent</span>
            </span>
            <span className={styles.Stat}>
              <span className={styles.StatValue}>{stats.skippedUnmapped}</span>
              <span className={styles.StatLabel}>nobody linked</span>
            </span>
            <span className={styles.Stat}>
              <span className={styles.StatValue}>{stats.skippedMuted}</span>
              <span className={styles.StatLabel}>silenced</span>
            </span>
            <span className={styles.Stat}>
              <span className={styles.StatValue}>{stats.skippedSuppressed}</span>
              <span className={styles.StatLabel}>held back by a rule</span>
            </span>
            <span className={styles.Stat}>
              <span className={styles.StatValue}>{stats.failed}</span>
              <span className={styles.StatLabel}>failed</span>
            </span>
          </div>
          <p className={styles.Hint}>
            {stats.peopleReached === 0
              ? 'Nobody has been reached this week. If that is unexpected, the delivery channel is the first place to look.'
              : 'Held back by a rule covers messages about your own actions and anything sent while a cycle was still a draft.'}
          </p>
        </div>
      )}
    </div>
  );
}
